"use client";

import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import Select from "react-select";
import {
  XMarkIcon,
  DocumentArrowDownIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from "@heroicons/react/20/solid";
import {
  fetchPriceUpdateLogs,
  downloadPriceUpdateXlsx,
  downloadPriceUpdateCsv,
} from "./priceUpdateExport";

const API = "https://erp-xe9u.onrender.com/api";

const cls = (...c) => c.filter(Boolean).join(" ");

const fmtPrice = (n) =>
  n === null || n === undefined
    ? "—"
    : `$${Number(n).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      })}`;

const fmtWhen = (d) => {
  const x = new Date(d);
  return isNaN(x)
    ? ""
    : x.toLocaleString("en-CA", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

const selectStyles = {
  control: (p, s) => ({
    ...p,
    backgroundColor: "#111827",
    borderColor: s.isFocused ? "#f97316" : "#374151",
    boxShadow: "none",
    "&:hover": { borderColor: "#f97316" },
    minHeight: "36px",
    fontSize: "13px",
    borderRadius: "0.5rem",
    minWidth: "220px",
  }),
  input: (p) => ({ ...p, color: "#e5e7eb" }),
  singleValue: (p) => ({ ...p, color: "#e5e7eb" }),
  placeholder: (p) => ({ ...p, color: "#6b7280" }),
  option: (p, s) => ({
    ...p,
    backgroundColor: s.isFocused ? "#1f2937" : "#111827",
    color: s.isSelected ? "#f97316" : "#e5e7eb",
    fontSize: "13px",
    cursor: "pointer",
  }),
  menu: (p) => ({
    ...p,
    backgroundColor: "#111827",
    border: "1px solid #1f2937",
  }),
  menuPortal: (p) => ({ ...p, zIndex: 100000 }),
};

const MATCH_BADGE = {
  sku: ["SKU", "bg-gray-700/60 text-gray-300 border-gray-600"],
  ai: ["AI", "bg-orange-500/15 text-orange-300 border-orange-500/30"],
  text: ["text", "bg-yellow-500/15 text-yellow-300 border-yellow-500/30"],
};

export default function PriceUpdateHistory({ vendors = [], onClose }) {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [vendor, setVendor] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState({}); // id -> full log
  const [busy, setBusy] = useState(false);

  const vendorOptions = useMemo(
    () => vendors.map((v) => ({ value: v._id, label: v.companyName })),
    [vendors],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({ limit: "200" });
      if (vendor) params.set("vendorId", vendor.value);
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      const res = await axios.get(
        `${API}/item-database/price-updates?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
      setSelected(new Set());
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [vendor, from, to]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !busy && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  const toggle = (id) =>
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const allSelected = logs.length > 0 && logs.every((l) => selected.has(l._id));
  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(logs.map((l) => l._id)));

  const openDetail = async (id) => {
    if (expanded === id) return setExpanded(null);
    setExpanded(id);
    if (detail[id]) return;
    try {
      const [full] = await fetchPriceUpdateLogs([id]);
      setDetail((d) => ({ ...d, [id]: full }));
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    }
  };

  const doExport = async (ids, kind) => {
    if (!ids.length) return;
    setBusy(true);
    setError("");
    try {
      const full = await fetchPriceUpdateLogs(ids);
      kind === "csv"
        ? downloadPriceUpdateCsv(full)
        : downloadPriceUpdateXlsx(full);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  const selectedIds = [...selected];

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl w-full max-w-6xl max-h-[92vh] flex flex-col">
        {/* header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-1 h-5 bg-orange-500 rounded-full" />
            <div>
              <h3 className="text-sm font-semibold text-white">
                Vendor Price Update History
              </h3>
              <p className="text-[11px] text-gray-500 mt-0.5">
                Every price / SKU change applied from a vendor price list ·
                export for your records or to share with estimators
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:text-white hover:bg-gray-800"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* filters */}
        <div className="px-5 py-3 border-b border-gray-800 shrink-0 flex flex-wrap items-center gap-3">
          <Select
            options={vendorOptions}
            value={vendor}
            onChange={setVendor}
            isClearable
            placeholder="All vendors"
            styles={selectStyles}
            menuPortalTarget={
              typeof document !== "undefined" ? document.body : null
            }
          />
          <label className="flex items-center gap-1.5 text-[11px] text-gray-500">
            From
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="px-2 py-1.5 text-sm bg-gray-950 border border-gray-800 rounded-lg text-gray-200 focus:border-orange-500 focus:outline-none [color-scheme:dark]"
            />
          </label>
          <label className="flex items-center gap-1.5 text-[11px] text-gray-500">
            To
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="px-2 py-1.5 text-sm bg-gray-950 border border-gray-800 rounded-lg text-gray-200 focus:border-orange-500 focus:outline-none [color-scheme:dark]"
            />
          </label>

          <div className="ml-auto flex items-center gap-2">
            <span className="text-[11px] text-gray-500">
              {selectedIds.length
                ? `${selectedIds.length} selected`
                : `${logs.length} of ${total} runs`}
            </span>
            <button
              onClick={() => doExport(selectedIds, "xlsx")}
              disabled={!selectedIds.length || busy}
              className="px-3 py-1.5 text-xs font-semibold text-white bg-orange-500 hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg flex items-center gap-1.5"
            >
              <DocumentArrowDownIcon className="h-4 w-4" />
              {busy ? "Preparing…" : "Export selected (Excel)"}
            </button>
            <button
              onClick={() => doExport(selectedIds, "csv")}
              disabled={!selectedIds.length || busy}
              className="px-3 py-1.5 text-xs text-gray-300 hover:text-white bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg disabled:opacity-40"
            >
              CSV
            </button>
          </div>
        </div>

        {error && (
          <div className="mx-5 mt-3 px-4 py-2 bg-red-900/30 border border-red-800 rounded-lg text-sm text-red-300 shrink-0">
            {error}
          </div>
        )}

        {/* list */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="py-20 flex justify-center">
              <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-orange-500" />
            </div>
          ) : logs.length === 0 ? (
            <div className="py-20 text-center text-sm text-gray-500">
              No price updates recorded yet.
            </div>
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr>
                  {[
                    <input
                      key="all"
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      className="accent-orange-500"
                    />,
                    "",
                    "When",
                    "Vendor",
                    "Source file",
                    "Applied by",
                    "Updated",
                    "▲",
                    "▼",
                    "SKUs",
                    "Added",
                    "",
                  ].map((h, i) => (
                    <th
                      key={i}
                      className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-gray-500 bg-gray-900 sticky top-0 z-10 border-b border-gray-800 text-left whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => {
                  const open = expanded === l._id;
                  const full = detail[l._id];
                  return (
                    <FragmentRow key={l._id}>
                      <tr
                        className={cls(
                          "border-b border-gray-800/60 hover:bg-gray-800/40",
                          selected.has(l._id) && "bg-orange-500/5",
                        )}
                      >
                        <td className="px-3 py-2 w-8">
                          <input
                            type="checkbox"
                            checked={selected.has(l._id)}
                            onChange={() => toggle(l._id)}
                            className="accent-orange-500"
                          />
                        </td>
                        <td className="px-1 py-2 w-6">
                          <button
                            onClick={() => openDetail(l._id)}
                            className="text-gray-500 hover:text-white"
                            title="Show items"
                          >
                            {open ? (
                              <ChevronDownIcon className="h-4 w-4" />
                            ) : (
                              <ChevronRightIcon className="h-4 w-4" />
                            )}
                          </button>
                        </td>
                        <td className="px-3 py-2 text-[12px] text-gray-300 whitespace-nowrap">
                          {fmtWhen(l.appliedAt)}
                        </td>
                        <td className="px-3 py-2 text-[12px] text-gray-200">
                          {l.vendorName}
                        </td>
                        <td className="px-3 py-2 text-[11px] text-gray-500 max-w-[220px] truncate">
                          {l.fileName || "—"}
                        </td>
                        <td className="px-3 py-2 text-[12px] text-gray-400">
                          {l.appliedByName || "—"}
                        </td>
                        <td className="px-3 py-2 font-mono text-[12px] text-gray-200">
                          {l.counts?.applied ?? 0}
                        </td>
                        <td className="px-3 py-2 font-mono text-[12px] text-red-400">
                          {l.counts?.increases ?? 0}
                        </td>
                        <td className="px-3 py-2 font-mono text-[12px] text-green-400">
                          {l.counts?.decreases ?? 0}
                        </td>
                        <td className="px-3 py-2 font-mono text-[12px] text-gray-300">
                          {l.counts?.skuChanged ?? 0}
                        </td>
                        <td className="px-3 py-2 font-mono text-[12px] text-gray-300">
                          {l.counts?.vendorEntriesAdded ?? 0}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-right">
                          <button
                            onClick={() => doExport([l._id], "xlsx")}
                            disabled={busy}
                            className="px-2 py-1 text-[11px] font-semibold text-orange-300 hover:text-orange-200 bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/30 rounded-md disabled:opacity-40"
                          >
                            Excel
                          </button>{" "}
                          <button
                            onClick={() => doExport([l._id], "csv")}
                            disabled={busy}
                            className="px-2 py-1 text-[11px] text-gray-400 hover:text-white bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-md disabled:opacity-40"
                          >
                            CSV
                          </button>
                        </td>
                      </tr>

                      {open && (
                        <tr className="bg-gray-950/60">
                          <td colSpan={12} className="px-6 py-3">
                            {!full ? (
                              <p className="text-[11px] text-gray-500">
                                Loading…
                              </p>
                            ) : (
                              <div className="max-h-72 overflow-auto border border-gray-800 rounded-lg">
                                <table className="w-full text-[11px]">
                                  <thead>
                                    <tr className="text-gray-500 text-left">
                                      {[
                                        "Item",
                                        "Vendor SKU",
                                        "Previous",
                                        "New",
                                        "Change",
                                        "Match",
                                      ].map((h) => (
                                        <th
                                          key={h}
                                          className="px-3 py-1.5 sticky top-0 bg-gray-950 font-semibold uppercase tracking-wider text-[10px]"
                                        >
                                          {h}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(full.entries || [])
                                      .slice(0, 500)
                                      .map((e, i) => {
                                        const [label, badge] =
                                          MATCH_BADGE[e.matchType] ||
                                          MATCH_BADGE.sku;
                                        return (
                                          <tr
                                            key={i}
                                            className="border-t border-gray-800/60"
                                          >
                                            <td className="px-3 py-1.5">
                                              <span className="font-mono text-orange-400">
                                                {e.itemCode}
                                              </span>{" "}
                                              <span className="text-gray-500">
                                                {e.description}
                                              </span>
                                            </td>
                                            <td className="px-3 py-1.5 font-mono text-gray-300">
                                              {e.skuChanged &&
                                                e.previousSku && (
                                                  <span className="text-gray-600 line-through mr-1">
                                                    {e.previousSku}
                                                  </span>
                                                )}
                                              {e.vendorSku}
                                            </td>
                                            <td className="px-3 py-1.5 font-mono text-gray-500">
                                              {e.vendorEntryAdded
                                                ? "new entry"
                                                : fmtPrice(e.previousPrice)}
                                            </td>
                                            <td className="px-3 py-1.5 font-mono text-gray-200">
                                              {fmtPrice(e.newPrice)}/{e.uom}
                                            </td>
                                            <td
                                              className={cls(
                                                "px-3 py-1.5 font-mono",
                                                e.change > 0 && "text-red-400",
                                                e.change < 0 &&
                                                  "text-green-400",
                                                !e.change && "text-gray-500",
                                              )}
                                            >
                                              {e.changePct === null ||
                                              e.changePct === undefined
                                                ? "—"
                                                : `${e.changePct > 0 ? "+" : ""}${e.changePct.toFixed(2)}%`}
                                            </td>
                                            <td className="px-3 py-1.5">
                                              <span
                                                className={cls(
                                                  "px-1.5 py-0.5 text-[9px] font-semibold rounded border",
                                                  badge,
                                                )}
                                              >
                                                {label}
                                                {e.matchType !== "sku" &&
                                                e.confidence != null
                                                  ? ` ${e.confidence}%`
                                                  : ""}
                                              </span>
                                              {e.status === "failed" && (
                                                <span className="ml-1 text-red-400">
                                                  failed: {e.error}
                                                </span>
                                              )}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                  </tbody>
                                </table>
                                {(full.entries || []).length > 500 && (
                                  <p className="px-3 py-2 text-[10px] text-gray-500">
                                    Showing first 500 of {full.entries.length} —
                                    export for the full list.
                                  </p>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </FragmentRow>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// tiny helper so a <tr> pair can share one key
function FragmentRow({ children }) {
  return <>{children}</>;
}
